"use client";

import { c as _c } from "react/compiler-runtime";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { usePostHog } from "posthog-js/react";
import { useEffect, useRef } from "react";
import { useUser } from "@/hooks/useUser";

/**
 * Fires `user_signed_up` for OAuth registrations.
 *
 * The email path captures the event inline in the signup form, but a social
 * sign-up redirects to the provider and back, so the signup page is gone by the
 * time the account exists. better-auth sends brand-new accounts to
 * `newUserCallbackURL` with `?signup_method=<provider>` (see SocialAuthButtons);
 * this reads that param wherever the user lands, emits the same event, and
 * strips the param so a refresh can't double-count.
 *
 * Mounted at the root, so the `/user/me` lookup stays gated on the param being
 * present — otherwise every marketing pageview would pay for a session fetch.
 */
export function SignupMethodTracker() {
  const $ = _c(12);
  const posthog = usePostHog();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  let t0;
  if ($[0] !== searchParams) {
    t0 = searchParams.get("signup_method");
    $[0] = searchParams;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const signupMethod = t0;
  const t1 = Boolean(signupMethod);
  let t2;
  if ($[2] !== t1) {
    t2 = {
      enabled: t1
    };
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const {
    user
  } = useUser(t2);
  const tracked = useRef(false);
  let t3;
  let t4;
  if ($[4] !== pathname || $[5] !== posthog || $[6] !== router || $[7] !== searchParams || $[8] !== signupMethod || $[9] !== user) {
    t3 = () => {
      if (!signupMethod || !user || tracked.current) {
        return;
      }
      tracked.current = true;
      posthog.capture("user_signed_up", {
        email: user.email,
        name: user.name,
        method: signupMethod
      });
      const params = new URLSearchParams(searchParams.toString());
      params.delete("signup_method");
      const query = params.toString();
      router.replace(query ? `${pathname}?${query}` : pathname);
    };
    t4 = [signupMethod, user, posthog, searchParams, router, pathname];
    $[4] = pathname;
    $[5] = posthog;
    $[6] = router;
    $[7] = searchParams;
    $[8] = signupMethod;
    $[9] = user;
    $[10] = t3;
    $[11] = t4;
  } else {
    t3 = $[10];
    t4 = $[11];
  }
  useEffect(t3, t4);
  return null;
}